import { useState, useEffect, useContext } from "react";
import * as XLSX from "xlsx";
import moment from "moment";
import { ClinicaContext } from "../../../../shared/context/clinica-context";
import { AuthContext } from "../../../../shared/context/auth-context";
import { useLoading } from "../../../../shared/context/LoadingContext";
import toast from "react-hot-toast";

// Constantes
const UNIDADES_MEDIDA = [
  { value: "UN", label: "Unidade" },
  { value: "KG", label: "Quilograma (kg)" },
  { value: "M", label: "Metro (m)" },
  { value: "L", label: "Litro (l)" },
  { value: "M3", label: "Metro Cúbico (m³)" },
  { value: "M2", label: "Metro Quadrado (m²)" },
  { value: "CX", label: "Caixa" },
];

const FORMAS_PAGAMENTO = [
  "Dinheiro",
  "Cheque",
  "Cheque Visado",
  "Transferência de crédito",
  "Internet banking",
  "Cartão do banco",
  "Débito direto da conta bancária",
  "Cartão de crédito",
  "Cartão de débito",
];

// Mapeamento de colunas para facilitar o reconhecimento de diferentes formatos
const COLUMN_MAPPINGS = {
  // Variações para "nome"
  nome: "nome",
  produto: "nome",
  item: "nome",
  descrição: "nome",
  descricao: "nome",

  // Variações para "quantidade"
  quantidade: "quantidade",
  qtd: "quantidade",
  qtde: "quantidade",
  qty: "quantidade",

  // Variações para "quantidade ideal"
  "quantidade ideal": "quantidadeIdeal",
  quantidadeideal: "quantidadeIdeal",
  "qtd ideal": "quantidadeIdeal",
  "estoque mínimo": "quantidadeIdeal",
  "estoque minimo": "quantidadeIdeal",
  "stock minimo": "quantidadeIdeal",
  "stock min": "quantidadeIdeal",

  // Variações para "unidadeMedida"
  unidademedida: "unidadeMedida",
  "unidade medida": "unidadeMedida",
  medida: "unidadeMedida",
  unidade: "unidadeMedida",

  // Variações para "lote"
  lote: "lote",
  lotes: "lote",
  batch: "lote",
  "número de lote": "lote",
  "numero de lote": "lote",

  // Variações para "dataValidade"
  datavalidade: "dataValidade",
  "data validade": "dataValidade",
  validade: "dataValidade",
  vencimento: "dataValidade",

  // Variações para "preço"
  preco: "precoCompra",
  preço: "precoCompra",
  "preço unitário": "precoCompra",
  "preco unitario": "precoCompra",
  custo: "precoCompra",
  valor: "precoCompra",

  // Variações para "fornecedor"
  fornecedor: "fornecedor",
  distribuidor: "fornecedor",
  "empresa fornecedora": "fornecedor",
  vendor: "fornecedor",

  // Novas variações para "classificação"
  classificacao: "classificacao",
  classificação: "classificacao",
  categoria: "classificacao",
  tipo: "classificacao",
  "tipo de produto": "classificacao",
  classificacao_produto: "classificacao",
  class: "classificacao",

  // Stock mínimo — como aparece nos ficheiros reais
  "stock mínimo": "quantidadeIdeal",
  "estoque min": "quantidadeIdeal",
  mínimo: "quantidadeIdeal",
  minimo: "quantidadeIdeal",
  "quantidade mínima": "quantidadeIdeal",
  "quantidade minima": "quantidadeIdeal",
  "qtd mínima": "quantidadeIdeal",
  "qtd minima": "quantidadeIdeal",
  "qtd min": "quantidadeIdeal",

  // Preço de compra — a coluna quase nunca se chama só "preço"
  "preço compra": "precoCompra",
  "preco compra": "precoCompra",
  "preço de compra": "precoCompra",
  "preco de compra": "precoCompra",
  "preço unitario": "precoCompra",
  "preco unitário": "precoCompra",
  "valor compra": "precoCompra",
  "valor de compra": "precoCompra",
  "custo unitário": "precoCompra",
  "custo unitario": "precoCompra",
  "custo de compra": "precoCompra",
};

/**
 * Chave com que se procura uma coluna na tabela acima.
 *
 * A procura era `columnName.toLowerCase().trim()` contra chaves escritas à mão,
 * e isso falha por coisas que ninguém vê no ficheiro:
 *
 *   - ACENTOS. A tabela tinha "stock minimo" sem acento; uma coluna escrita
 *     «Stock mínimo» não casava com nada e ficava por mapear.
 *   - ACENTOS ESCRITOS DE OUTRA MANEIRA. O mesmo «ç» pode estar guardado como
 *     um caracter só ou como «c» mais uma cedilha separada — parecem iguais no
 *     ecrã e são diferentes para o computador. O Excel produz as duas formas
 *     conforme a origem do ficheiro, e é por isso que «Classificação», que até
 *     estava na tabela, podia na mesma não ser reconhecida.
 *   - ESPAÇOS A MAIS, ou um espaço invisível colado ao fim do cabeçalho.
 *
 * Tira os acentos, junta os espaços repetidos e ignora maiúsculas. A tabela
 * acima continua escrita como se lê — é normalizada uma vez, aqui.
 */
const normalizarCabecalho = (texto) =>
  String(texto ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[_.:;]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

// Uma só vez, no arranque, em vez de a cada coluna de cada ficheiro.
const MAPA_COLUNAS_NORMALIZADO = Object.entries(COLUMN_MAPPINGS).reduce(
  (mapa, [chave, destino]) => {
    mapa[normalizarCabecalho(chave)] = destino;
    return mapa;
  },
  {},
);

/**
 * Campos que o mapeamento automático pode preencher.
 *
 * Sai dos DESTINOS da tabela acima, e não de uma lista escrita à mão ao lado.
 * A lista à mão que aqui estava tinha dois defeitos, e ambos se viam no ecrã:
 *
 *   - dizia `"preco"`, mas a tabela mapeia os preços para `"precoCompra"`.
 *     Nenhuma coluna de preço era alguma vez reconhecida — nem «Preço», que
 *     estava na tabela desde o início.
 *   - não tinha `"classificacao"`. A coluna era traduzida corretamente e
 *     descartada logo a seguir, por não constar desta segunda lista.
 *
 * Duas listas a ter de concordar uma com a outra é o que produz este tipo de
 * erro. Agora há uma só: acrescentar uma variante à tabela chega para a coluna
 * passar a ser reconhecida.
 */
const CAMPOS_MAPEAVEIS = new Set(Object.values(COLUMN_MAPPINGS));

/**
 * Chave com que se decide se duas linhas falam do mesmo produto.
 *
 * Era `nome.trim().toLowerCase()`, e isso deixava passar como produtos
 * diferentes coisas que uma pessoa lê como iguais: "GUTTA PERCHA  F1" com dois
 * espaços, "Algodão" contra "Algodao", um espaço invisível colado no fim de uma
 * célula do Excel. Cada uma dessas diferenças criava um produto novo, a
 * quantidade ficava repartida entre as cópias e os alertas de reposição
 * passavam a mentir.
 *
 * Tira acentos, junta espaços repetidos e ignora maiúsculas.
 */
const chaveDoProduto = (nome) =>
  String(nome || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

function EnhancedImportStock({
  stock,
  handleCloseModal,
  sendRequest,
  atualizarStock,
  categoriasStock,
}) {
  const [fornecedoresList, setFornecedoresList] = useState([]);
  const [armazensListImport, setArmazensListImport] = useState([]);
  const [localizacoesList, setLocalizacoesList] = useState([]);

  const { startLoading, stopLoading } = useLoading();
  const clinica = useContext(ClinicaContext);
  const auth = useContext(AuthContext);

  // Estados principais
  const [currentStep, setCurrentStep] = useState(1);
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [processedData, setProcessedData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [categoriasFinanceiro, setCategoriasFinanceiro] = useState([]);
  const [caixas, setCaixas] = useState([]);
  const [fornecedorDespesa, setFornecedorDespesa] = useState("");

  // Estados para configuração
  const [categoriaPrincipal, setCategoriaPrincipal] = useState("");
  const [adicionarComoDespesa, setAdicionarComoDespesa] = useState(false);
  const [despesaPaga, setDespesaPaga] = useState(true);
  const [custoTotal, setCustoTotal] = useState(0);
  const [caixa, setCaixa] = useState("");
  const [categoria, setCategoria] = useState("");
  const [descricao, setDescricao] = useState("");
  const [metodoPagamento, setMetodoPagamento] = useState(FORMAS_PAGAMENTO[0]);

  // Estados para gerenciamento de colunas
  const [detectedColumns, setDetectedColumns] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [hasHeaderRow, setHasHeaderRow] = useState(true);

  const [armazemPadrao, setArmazemPadrao] = useState("");
  const [localizacaoPadrao, setLocalizacaoPadrao] = useState("");

  const [isProcessingData, setIsProcessingData] = useState(false);
  // Estados para validação
  const [errors, setErrors] = useState({
    columnErrors: [],
    rowErrors: [],
  });

  const [usarLotesAutomaticos, setUsarLotesAutomaticos] = useState(true);

  // Função para buscar fornecedores, modificada para armazenar os resultados
  const fetchFornecedores = async () => {
    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/fornecedores/clinica/stock/${clinica.clinica._id}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      );

      const listaFornecedores = responseData.fornecedores || [];
      // Armazenar no estado para uso posterior
      setFornecedoresList(listaFornecedores);
      return listaFornecedores;
    } catch (err) {
      console.error("Erro ao buscar fornecedores:", err);
      return [];
    }
  };

  const fetchArmazensELocalizacoes = async () => {
    try {
      const [resArm, resLoc] = await Promise.all([
        sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/armazens/clinica/${clinica.clinica._id}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        ),
        sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/localizacoes/clinica/${clinica.clinica._id}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        ),
      ]);
      const armazensAtivos = (resArm.armazens || []).filter((a) => a.ativo);
      const locsAtivas = (resLoc.localizacoes || []).filter((l) => l.ativo);
      setArmazensListImport(armazensAtivos);
      setLocalizacoesList(locsAtivas);

      if (armazensAtivos.length > 0) {
        const primArm = armazensAtivos[0];
        const primLoc = locsAtivas.find(
          (l) => l.armazem?.id === primArm.id || l.armazem === primArm.id,
        );
        setArmazemPadrao(primArm.id);
        setLocalizacaoPadrao(primLoc?.id || "");
      }
    } catch (err) {
      console.error("Erro ao carregar armazéns e localizações:", err);
    }
  };

  // Carregar fornecedores ao inicializar o componente
  useEffect(() => {
    if (clinica?.clinica?._id && auth?.token) {
      fetchFornecedores();
      fetchArmazensELocalizacoes();
    }
  }, [clinica?.clinica?._id, auth?.token]);

  // Buscar dados necessários ao montar o componente
  useEffect(() => {
    const fetchData = async () => {
      startLoading();
      try {
        // Use as categorias passadas como prop
        if (categoriasStock && categoriasStock.length > 0) {
          // Configurar categoria principal com a primeira categoria
          setCategoriaPrincipal(categoriasStock[0]._id);
        }

        const [categoriasFinanceiroResponse, caixasResponse] =
          await Promise.all([
            sendRequest(
              `${process.env.REACT_APP_BACKEND_LINK}/api/categoriasfinanceiro/clinica/despesa/${clinica.clinica._id}`,
              "GET",
              null,
              { Authorization: "Bearer " + auth.token },
            ),
            sendRequest(
              `${process.env.REACT_APP_BACKEND_LINK}/api/caixas/clinica/${clinica.clinica._id}`,
              "GET",
              null,
              { Authorization: "Bearer " + auth.token },
            ),
          ]);

        // Configurar categorias financeiras e caixas para despesa
        if (categoriasFinanceiroResponse.categoriasFinanceiro) {
          setCategoriasFinanceiro(
            categoriasFinanceiroResponse.categoriasFinanceiro.map((cat) => ({
              id: cat.id,
              value: cat.categoria,
            })),
          );
          if (categoriasFinanceiroResponse.categoriasFinanceiro.length > 0) {
            setCategoria(
              categoriasFinanceiroResponse.categoriasFinanceiro[0].id,
            );
          }
        }

        if (caixasResponse.caixas) {
          setCaixas(
            caixasResponse.caixas.map((cai) => ({
              id: cai.id,
              value: cai.caixa,
            })),
          );
          if (caixasResponse.caixas.length > 0) {
            setCaixa(caixasResponse.caixas[0].id);
          }
        }
      } catch (err) {
        console.error("Erro ao buscar dados:", err);
        toast.error("Erro ao carregar dados necessários");
      } finally {
        stopLoading();
      }
    };

    fetchData();
  }, [sendRequest, clinica, auth, categoriasStock]);

  useEffect(() => {
    if (!armazemPadrao || processedData.length === 0) return;
    setProcessedData((prev) =>
      prev.map((item) => ({
        ...item,
        armazem: item.armazem || armazemPadrao,
        localizacao: item.localizacao || localizacaoPadrao,
      })),
    );
  }, [armazemPadrao, localizacaoPadrao]);

  // Normaliza o nome da coluna para um padrão reconhecido
  const normalizeColumnName = (columnName) => {
    if (!columnName) return "";

    const normalized = normalizarCabecalho(columnName);

    // Retorna o nome mapeado ou o original
    return MAPA_COLUNAS_NORMALIZADO[normalized] || normalized;
  };

  // Normaliza a unidade de medida
  const normalizeUnidadeMedida = (unit) => {
    if (!unit) return "UN";

    // Remover espaços e converter para minúsculo
    const normalized = unit.toString().toLowerCase().trim();

    // Mapear unidades comuns
    const unitMappings = {
      ea: "UN",
      un: "UN",
      unidade: "UN",
      cada: "UN",
      unit: "UN",
      kg: "KG",
      kgs: "KG",
      kilos: "KG",
      kilogramas: "KG",
      kilogram: "KG",
      m: "M",
      metro: "M",
      metros: "M",
      l: "L",
      lt: "L",
      litro: "L",
      litros: "L",
      m3: "M3",
      "m³": "M3",
      "metro cubico": "M3",
      "metros cubicos": "M3",
      m2: "M2",
      "m²": "M2",
      "metro quadrado": "M2",
      "metros quadrados": "M2",
      cx: "CX",
      caixa: "CX",
      caixas: "CX",
      box: "CX",
    };

    return unitMappings[normalized] || "UN";
  };

  // Converte com segurança valores para números
  const safeNumberConversion = (value) => {
    // Tratar valor vazio ou undefined
    if (value === undefined || value === null || value === "") {
      return 0;
    }

    // Converter para string para manipulação
    const strValue = String(value).trim();

    // Verificar se é apenas traços ou caracteres inválidos
    if (/^\s*[-–—]+\s*$/.test(strValue)) {
      return 0;
    }

    // Substituir vírgulas por pontos e tentar converter
    const cleanValue = strValue.replace(",", ".");
    const numberValue = Number(cleanValue);

    // Retornar 0 se não for um número válido
    return isNaN(numberValue) ? 0 : numberValue;
  };

  // Processar data Excel para data JavaScript
  const processExcelDate = (excelDate) => {
    if (!excelDate) return null;

    try {
      // Verificar se já é um objeto de data
      if (excelDate instanceof Date && !isNaN(excelDate)) {
        return excelDate;
      }

      // Verificar se é uma string de data no formato DD/MM/YYYY
      if (typeof excelDate === "string") {
        const texto = excelDate.trim();

        /* ── SÓ MÊS E ANO ─────────────────────────────────────────────────
         *
         * É como a validade vem impressa na esmagadora maioria das caixas:
         * «05/2027», e não um dia certo. O que isso quer dizer é que o produto
         * se pode usar até ao FIM desse mês — por isso a data guardada é o
         * último dia, e não o primeiro.
         *
         * Com o dia 1, uma caixa válida até ao fim de maio aparecia como
         * expirada durante o mês inteiro de maio, e era descartada um mês antes
         * do tempo.
         *
         * Isto vem ANTES das outras tentativas de propósito: «2027-05» era
         * aceite pelo formato «YYYY-MM-DD» e dava dia 1 sem se queixar. Já
         * «05/2027» nem sequer era reconhecida — a validade desaparecia em
         * silêncio, que é ainda pior do que estar um mês adiantada.
         * --------------------------------------------------------------- */
        const mesAno = texto.match(/^(\d{1,2})[/\-. ](\d{4})$/); // 05/2027
        const anoMes = texto.match(/^(\d{4})[/\-. ](\d{1,2})$/); // 2027-05

        const mes = mesAno ? Number(mesAno[1]) : anoMes ? Number(anoMes[2]) : null;
        const ano = mesAno ? Number(mesAno[2]) : anoMes ? Number(anoMes[1]) : null;

        if (mes !== null && mes >= 1 && mes <= 12) {
          return moment({ year: ano, month: mes - 1 })
            .endOf("month")
            .startOf("day")
            .toDate();
        }

        // Tentar formato DD/MM/YYYY ou YYYY-MM-DD
        const momentDate = moment(texto, [
          "DD/MM/YYYY",
          "YYYY-MM-DD",
          "DD-MM-YYYY",
        ]);
        if (momentDate.isValid()) {
          return momentDate.toDate();
        }
      }

      // Tratar número serial do Excel
      if (typeof excelDate === "number") {
        // O Excel usa 25569 como diferença para datas (01/01/1970)
        // Ajuste de 2 dias por causa do fuso horário
        const date = new Date((excelDate - (25567 + 2)) * 86400 * 1000);
        return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
      }

      return null;
    } catch (error) {
      console.error("Erro ao processar data:", error);
      return null;
    }
  };

  // Handle file change
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    // Validar tipo de arquivo
    const allowedExtensions = /(\.xls|\.xlsx)$/i;

    if (!allowedExtensions.test(selectedFile.name)) {
      setErrorMessage(
        `O arquivo "${selectedFile.name}" tem uma extensão inválida. Use .xls ou .xlsx`,
      );
      return;
    }

    // Validar tamanho (1MB max)
    const maxSizeInBytes = 1 * 1024 * 1024;
    if (selectedFile.size > maxSizeInBytes) {
      setErrorMessage(
        `O arquivo "${selectedFile.name}" excede o tamanho máximo de 1MB.`,
      );
      return;
    }

    setFile(selectedFile);
    setErrorMessage("");
    readFile(selectedFile);
  };

  // Ler e processar o arquivo
  const readFile = (selectedFile) => {
    startLoading();
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        // O resultado agora é um ArrayBuffer
        const arrayBuffer = event.target.result;

        // Processe o Excel com XLSX
        const workbook = XLSX.read(arrayBuffer, {
          type: "array", // Mude type para 'array' ao invés de 'binary'
          cellDates: true,
          cellNF: false,
          cellText: false,
        });

        // O resto do seu código permanece igual
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
          blankrows: false,
        });

        processFileData(jsonData);
      } catch (error) {
        setErrorMessage(`Erro ao ler o arquivo: ${error.message}`);
        stopLoading();
      }
    };

    reader.onerror = () => {
      setErrorMessage(
        "Erro ao ler o arquivo. Verifique se o arquivo não está corrompido.",
      );
      stopLoading();
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  // Processar dados do arquivo
  const processFileData = (rawData) => {
    if (!rawData || rawData.length === 0) {
      setErrorMessage("O arquivo está vazio ou em formato inválido.");
      stopLoading();
      return;
    }

    setOriginalData(rawData);

    // Detectar e mapear colunas
    let headerRow = hasHeaderRow ? rawData[0] : [];

    // Mapear automaticamente as colunas se houver cabeçalhos
    if (hasHeaderRow && headerRow.length > 0) {
      const detectedCols = headerRow.map((col) => {
        if (!col) return { original: "", normalized: "" };
        const normalized = normalizeColumnName(col.toString());
        return {
          original: col.toString(),
          normalized,
        };
      });

      setDetectedColumns(detectedCols);

      // Criar mapeamento automático inicial
      const initialMapping = {};
      detectedCols.forEach((col, index) => {
        if (CAMPOS_MAPEAVEIS.has(col.normalized)) {
          initialMapping[index] = col.normalized;
        }
      });

      setColumnMapping(initialMapping);

      // Tentar processar os dados com o mapeamento automático
      const dataStartIndex = hasHeaderRow ? 1 : 0;
      tryProcessDataWithMapping(rawData.slice(dataStartIndex), initialMapping);
    } else {
      // Se não tem cabeçalho ou não foi possível detectar, criar colunas vazias
      const columnCount = rawData[0]?.length || 0;
      const generatedCols = Array(columnCount)
        .fill(0)
        .map((_, i) => ({
          original: `Coluna ${i + 1}`,
          normalized: "",
        }));

      setDetectedColumns(generatedCols);
      setColumnMapping({});
    }

    stopLoading();
  };

  const normalizeClassificacao = (classificacao) => {
    if (!classificacao) return "PA";

    // Remover espaços e converter para minúsculo
    const normalized = classificacao.toString().toLowerCase().trim();

    // Mapear classificações comuns
    if (
      normalized.includes("prima") ||
      normalized.includes("materia") ||
      normalized.includes("matéria") ||
      normalized.includes("raw")
    ) {
      return "MP";
    }

    return "PA";
  };

  // Tentar processar os dados com um mapeamento de colunas
  const tryProcessDataWithMapping = (dataRows, mapping) => {
    if (!dataRows || dataRows.length === 0) return;

    setIsProcessingData(true);

    try {
      // Verificar se temos o mínimo de mapeamento necessário
      const values = Object.values(mapping).filter(
        (v) => v !== null && v !== undefined,
      );
      const hasMandatoryMapping =
        values.includes("nome") && values.includes("quantidade");

      if (!hasMandatoryMapping) {
        // Sem mapeamento essencial, não podemos processar ainda
        setProcessedData([]);
        return;
      }

      // Garantir que temos uma categoria principal
      const defaultCategoryId =
        categoriaPrincipal ||
        (categoriasStock && categoriasStock.length > 0
          ? categoriasStock[0].id
          : "");

      // Processar os dados com o mapeamento atual
      const processedItems = dataRows.map((row, rowIndex) => {
        const item = {
          id: `row-${rowIndex}`,
          nome: "",
          quantidade: 0,
          quantidadeIdeal: null,
          lote: "N/A",
          dataValidade: null,
          categoriaStock: defaultCategoryId,
          unidadeMedida: "UN",
          // Novos campos
          classificacao: "PA",
          fornecedor: "", // Inicializado vazio, ignorando o que vem do XLS
          precoCompra: 0,
          armazem: "",
          localizacao: "",
          _original: { rowIndex, data: row },
        };

        // Aplicar mapeamento
        Object.entries(mapping).forEach(([colIndex, fieldName]) => {
          const value = row[Number(colIndex)];

          switch (fieldName) {
            case "nome":
              item.nome = value?.toString() || "";
              break;
            case "quantidade":
              item.quantidade = safeNumberConversion(value);
              break;
            case "quantidadeIdeal":
              item.quantidadeIdeal = safeNumberConversion(value);
              break;
            case "lote":
              item.lote = value?.toString() || "N/A";
              break;
            case "dataValidade":
              item.dataValidade = processExcelDate(value);
              break;
            case "unidadeMedida":
              item.unidadeMedida = normalizeUnidadeMedida(value);
              break;
            case "precoCompra":
              item.precoCompra = safeNumberConversion(value);
              break;
            case "fornecedor":
              // Ignoramos propositalmente o valor e mantemos vazio
              // item.fornecedorNome = value?.toString() || "";
              break;
            case "classificacao":
              item.classificacao = normalizeClassificacao(value);
              break;
            default:
              // Ignore unknown fields
              break;
          }
        });

        return item;
      });

      const itemsComLocalizacao = processedItems.map((item) => ({
        ...item,
        armazem: item.armazem || armazemPadrao,
        localizacao: item.localizacao || localizacaoPadrao,
      }));

      setProcessedData(itemsComLocalizacao);

      // Validar os dados processados
      validateProcessedData(itemsComLocalizacao);

      // Atualizar custo total se tiver informação de preço
      const totalCost = processedItems.reduce((sum, item) => {
        return sum + (item.precoCompra || 0) * item.quantidade;
      }, 0);

      if (totalCost > 0) {
        setCustoTotal(totalCost);
      }
    } catch (error) {
      console.error("Erro ao processar dados:", error);
      setErrorMessage("Erro ao processar dados do arquivo.");
    } finally {
      setIsProcessingData(false);
    }
  };

  // Validar os dados processados
  const validateProcessedData = (data) => {
    const rowErrors = [];

    data.forEach((item, index) => {
      const lineErrors = [];
      const lineNum = index + 1;

      // Validar nome
      if (!item.nome || item.nome.trim() === "") {
        lineErrors.push("Nome do produto não pode ser vazio");
      }

      // Validar quantidade
      if (item.quantidade < 0) {
        lineErrors.push(
          `Quantidade inválida ou não positiva: ${item.quantidade}`,
        );
      }

      // Validar quantidade ideal, se presente
      if (item.quantidadeIdeal !== null && item.quantidadeIdeal < 0) {
        lineErrors.push(
          `Stock mínimo não pode ser negativo: ${item.quantidadeIdeal}`,
        );
      }

      // Validar data de validade, se presente
      if (item.dataValidade && !(item.dataValidade instanceof Date)) {
        lineErrors.push(`Data de validade inválida`);
      }

      // Validar preço, se presente
      if (item.precoCompra < 0) {
        lineErrors.push(`Preço não pode ser negativo: ${item.precoCompra}`);
      }

      if (!item.armazem) {
        lineErrors.push("Armazém não selecionado");
      }

      if (!item.localizacao) {
        lineErrors.push("Localização não selecionada");
      }

      // Se houver erros nesta linha, adicionar ao array de erros
      if (lineErrors.length > 0) {
        rowErrors.push({
          linha: lineNum,
          produto: item.nome || `Linha ${lineNum}`,
          erros: lineErrors,
        });
      }
    });

    setErrors((prev) => ({ ...prev, rowErrors }));
  };

  // Atualizar o mapeamento de colunas
  const updateColumnMapping = (colIndex, fieldName) => {
    setColumnMapping((prev) => {
      const newMapping = { ...prev };

      // Se o campo for vazio (""), remover o mapeamento
      if (fieldName === "") {
        delete newMapping[colIndex];
      } else {
        newMapping[colIndex] = fieldName;
      }

      // Tentar processar dados com novo mapeamento
      const dataStartIndex = hasHeaderRow ? 1 : 0;
      tryProcessDataWithMapping(originalData.slice(dataStartIndex), newMapping);

      return newMapping;
    });
  };

  // Alternar opção de cabeçalho
  const toggleHeaderRow = () => {
    setHasHeaderRow(!hasHeaderRow);

    // Reprocessar os dados com a nova configuração
    if (originalData.length > 0) {
      processFileData(originalData);
    }
  };

  // Avançar para o próximo passo
  const handleNextStep = () => {
    if (currentStep === 1 && (!processedData || processedData.length === 0)) {
      toast.error(
        "Por favor, configure o mapeamento de colunas corretamente antes de continuar.",
      );
      return;
    }

    if (currentStep === 2) {
      setIsProcessingData(true);

      // Usar setTimeout para permitir que a interface seja atualizada antes do processamento pesado
      setTimeout(() => {
        try {
          // Qualquer processamento adicional necessário pode ser feito aqui
          // Por exemplo, pré-cálculos ou formatação dos dados

          // Avançar para o próximo passo
          setCurrentStep((prev) => Math.max(1, prev + 1));
        } catch (error) {
          console.error("Erro ao processar dados:", error);
          toast.error("Ocorreu um erro ao processar os dados");
        } finally {
          setIsProcessingData(false);
        }
      }, 100);
    } else {
      // Para outros passos, apenas avançar
      setCurrentStep((prev) => prev + 1);
    }
  };

  // Voltar ao passo anterior
  const handlePreviousStep = () => {
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  // Atualizar o valor de um item específico nos dados processados
  const updateProcessedItem = (itemId, field, value) => {
    setProcessedData((prevData) => {
      // Criar uma cópia do array de dados
      const updatedData = prevData.map((item) => {
        if (item.id === itemId) {
          // Se for atualização do fornecedor, fazer o tratamento específico
          if (field === "fornecedor") {
            // Se o valor for "new", não alterar o campo
            if (value === "new") {
              return item;
            }

            // Buscar o nome do fornecedor para exibição, se disponível
            let fornecedorNome = "";
            if (value) {
              const matchingFornecedor = fornecedoresList.find(
                (f) => f.id === value,
              );
              if (matchingFornecedor) {
                fornecedorNome = matchingFornecedor.nome;
              }
            }

            // Retornar o item com fornecedor atualizado
            return {
              ...item,
              [field]: value,
              fornecedorNome: fornecedorNome,
            };
          }

          // Para outros campos, apenas atualizar o valor
          return { ...item, [field]: value };
        }
        return item;
      });

      // Retornar o array atualizado
      return updatedData;
    });
  };

  // Função para aplicar fornecedor a todos os itens
  const handleFornecedorPadraoChange = (fornecedorId) => {
    if (!fornecedorId) return;

    setIsProcessingData(true);

    setTimeout(() => {
      try {
        setProcessedData((prevData) => {
          // Buscar o nome do fornecedor para exibição
          let fornecedorNome = "";
          const matchingFornecedor = fornecedoresList.find(
            (f) => f.id === fornecedorId,
          );
          if (matchingFornecedor) {
            fornecedorNome = matchingFornecedor.nome;
          }

          // Atualizar todos os itens com o fornecedor selecionado
          return prevData.map((item) => ({
            ...item,
            fornecedor: fornecedorId,
            fornecedorNome: fornecedorNome,
          }));
        });

        // Atualizar também o fornecedor da despesa
        setFornecedorDespesa(fornecedorId);
      } catch (error) {
        console.error("Erro ao aplicar fornecedor padrão:", error);
        toast.error("Ocorreu um erro ao aplicar o fornecedor");
      } finally {
        setIsProcessingData(false);
      }
    }, 100);
  };

  const handleArmazemPadraoChange = (armazemId) => {
    if (!armazemId) return;
    const primeiraLoc = localizacoesList.find(
      (l) => l.armazem?.id === armazemId || l.armazem === armazemId,
    );
    const novaLoc = primeiraLoc?.id || "";
    setArmazemPadrao(armazemId);
    setLocalizacaoPadrao(novaLoc);
    setProcessedData((prev) =>
      prev.map((item) => ({
        ...item,
        armazem: armazemId,
        localizacao: novaLoc,
      })),
    );
  };

  const handleLocalizacaoPadraoChange = (localizacaoId) => {
    if (!localizacaoId) return;
    setLocalizacaoPadrao(localizacaoId);
    setProcessedData((prev) =>
      prev.map((item) => ({ ...item, localizacao: localizacaoId })),
    );
  };

  // Estas funções serão usadas no componente para interação do usuário
  const handleCategoriaPrincipalChange = (categoriaId) => {
    if (categoriaId === null || categoriaId === undefined) {
      console.warn("ID de categoria inválido");
      return;
    }

    // Normalizar para garantir consistência
    const normalizedCategoriaId = String(categoriaId);

    setIsProcessingData(true);

    setTimeout(() => {
      try {
        setCategoriaPrincipal(normalizedCategoriaId);

        // Atualizar todos os itens com a categoria selecionada
        setProcessedData((prevData) => {
          const updatedData = prevData.map((item) => ({
            ...item,
            categoriaStock: normalizedCategoriaId,
          }));
          return updatedData;
        });
      } catch (error) {
        console.error("Erro ao aplicar categoria padrão:", error);
        toast.error("Ocorreu um erro ao aplicar a categoria");
      } finally {
        setIsProcessingData(false);
      }
    }, 100);
  };

  const gerarCodigoLoteAutomatico = (prefixo = "AUTO") => {
    const data = new Date();
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, "0");
    const dia = String(data.getDate()).padStart(2, "0");
    const timestamp = String(Date.now()).slice(-6); // Últimos 6 dígitos para diferenciação

    return `${prefixo}-${dia}${mes}${ano}-${timestamp}`;
  };

  // Submeter importação
  const handleSubmitImport = async () => {
    if (!processedData || processedData.length === 0) {
      toast.error("Nenhum dado disponível para importar.");
      return;
    }

    // Verificar limite de 100 itens
    if (processedData.length > 100) {
      toast.error(
        "Não é possível importar mais de 100 itens por vez. Por favor, reduza a quantidade de itens.",
      );
      return;
    }

    // Validar datas
    const invalidDates = processedData.some(
      (p) => p.dataValidade && !(p.dataValidade instanceof Date),
    );

    if (invalidDates) {
      toast.error(
        "Existem datas inválidas nos dados. Por favor corrija antes de importar.",
      );
      return;
    }

    startLoading();
    setIsSubmitting(true);

    // Gerar um lote automático para esta importação
    const loteImportacao = gerarCodigoLoteAutomatico();

    try {
      // Processar dados para importação
      const novosProdutos = [];
      const produtosExistentes = [];
      const movimentosStock = [];
      let updatedStock = [...stock.stock];

      // Criar mapa de produtos existentes para pesquisa rápida
      const produtosExistentesMap = {};
      stock.stock.forEach((stockItem) => {
        produtosExistentesMap[chaveDoProduto(stockItem.produto.nome)] =
          stockItem;
      });

      /* Produtos criados DURANTE esta importação.
         Sem isto, um nome que apareça em duas linhas do ficheiro (dois lotes,
         duas validades, duas localizações) não é encontrado na segunda volta —
         o mapa acima só conhece o que já estava no stock — e nasce um segundo
         produto com o mesmo nome. Era assim que uma importação sozinha deixava
         a lista cheia de duplicados. */
      const novosProdutosPorNome = new Map();

      for (const item of processedData) {
        // Verificar se o lote precisa ser automático
        const usarLoteAutomatico = !item.lote || item.lote === "N/A";
        const loteAtual = usarLoteAutomatico ? loteImportacao : item.lote;
        const isAutoGerado = usarLoteAutomatico;

        const nomeLowerCase = chaveDoProduto(item.nome);
        const produtoExistente = produtosExistentesMap[nomeLowerCase];

        if (produtoExistente) {
          // Processar produto existente
          const loteExistente = produtoExistente.lotes.find(
            (l) =>
              l.lote.trim().toLowerCase() === loteAtual.trim().toLowerCase(),
          );

          if (loteExistente) {
            // Atualizar lote existente
            loteExistente.quantidade += item.quantidade;
            if (item.dataValidade) {
              loteExistente.dataValidade = item.dataValidade;
            }
            // Atualizar preço e fornecedor se fornecidos
            if (item.precoCompra) {
              loteExistente.precoCompra = item.precoCompra;
            }
            if (item.fornecedor) {
              loteExistente.fornecedor = item.fornecedor;
            }
            // Atualizar localização
            if (item.localizacao) {
              const locIndex = loteExistente.localizacoes.findIndex(
                (l) =>
                  l.localizacao?.toString() === item.localizacao.toString(),
              );
              if (locIndex !== -1) {
                loteExistente.localizacoes[locIndex].quantidade +=
                  item.quantidade;
              } else {
                loteExistente.localizacoes.push({
                  localizacao: item.localizacao,
                  quantidade: item.quantidade,
                });
              }
            }
          } else {
            // Adicionar novo lote
            produtoExistente.lotes.push({
              lote: loteAtual,
              quantidade: item.quantidade,
              dataValidade: item.dataValidade,
              precoCompra: item.precoCompra || 0,
              fornecedor: item.fornecedor || null,
              autoGerado: isAutoGerado,
              dataImportacao: new Date(),
              localizacoes: [
                {
                  localizacao: item.localizacao || null,
                  quantidade: item.quantidade,
                },
              ],
            });
          }

          produtoExistente.quantidadeTotal += item.quantidade;

          // Atualizar quantidade ideal se fornecida
          if (item.quantidadeIdeal !== null) {
            produtoExistente.quantidadeIdeal = item.quantidadeIdeal;
          }

          produtosExistentes.push(produtoExistente);

          // Criar registro de movimento de estoque
          movimentosStock.push({
            clinica: clinica.clinica._id,
            produto: produtoExistente.produto._id,
            lote: loteAtual,
            dataValidade: item.dataValidade,
            responsavelMovimento: { tipo: "User", responsavel: auth.userId },
            quantidade: item.quantidade,
            data: new Date(),
            tipoMovimento: "entrada",
            precoCompra: item.precoCompra || 0,
            fornecedor: item.fornecedor || null,
            localizacaoDestino: item.localizacao || null,
          });
        } else {
          const novoLote = {
            lote: loteAtual,
            quantidade: item.quantidade,
            dataValidade: item.dataValidade,
            precoCompra: item.precoCompra || 0,
            fornecedor: item.fornecedor || null,
            autoGerado: isAutoGerado,
            dataImportacao: new Date(),
            localizacoes: [
              {
                localizacao: item.localizacao || null,
                quantidade: item.quantidade,
              },
            ],
          };

          const jaCriadoNestaImportacao = novosProdutosPorNome.get(
            nomeLowerCase,
          );

          if (jaCriadoNestaImportacao) {
            /* Segunda (ou terceira) linha do ficheiro para o mesmo produto:
               acumula no produto que já vai ser criado, em vez de criar outro. */
            const loteRepetido = jaCriadoNestaImportacao.lotes.find(
              (l) =>
                l.lote.trim().toLowerCase() === loteAtual.trim().toLowerCase(),
            );

            if (loteRepetido) {
              loteRepetido.quantidade += item.quantidade;
              if (item.dataValidade) loteRepetido.dataValidade = item.dataValidade;
              if (item.precoCompra) loteRepetido.precoCompra = item.precoCompra;
              if (item.fornecedor) loteRepetido.fornecedor = item.fornecedor;

              const loc = loteRepetido.localizacoes.find(
                (l) =>
                  String(l.localizacao || "") ===
                  String(item.localizacao || ""),
              );
              if (loc) loc.quantidade += item.quantidade;
              else
                loteRepetido.localizacoes.push({
                  localizacao: item.localizacao || null,
                  quantidade: item.quantidade,
                });
            } else {
              jaCriadoNestaImportacao.lotes.push(novoLote);
            }

            jaCriadoNestaImportacao.quantidadeTotal += item.quantidade;
            if (item.quantidadeIdeal !== null) {
              jaCriadoNestaImportacao.quantidadeIdeal = item.quantidadeIdeal;
            }
          } else {
            // Adicionar novo produto
            const novoProduto = {
              clinica: clinica.clinica._id,
              nome: item.nome,
              categoriaStock: item.categoriaStock,
              unidadeMedida: item.unidadeMedida,
              classificacao: item.classificacao || "PA",
              lotes: [novoLote],
              quantidadeTotal: item.quantidade,
              quantidadeIdeal:
                item.quantidadeIdeal !== null ? item.quantidadeIdeal : 2,
            };

            novosProdutos.push(novoProduto);
            novosProdutosPorNome.set(nomeLowerCase, novoProduto);
          }
        }
      }

      // Preparar novos produtos para o backend
      const novosProdutosBackend = novosProdutos.map((p) => ({
        clinica: p.clinica,
        nome: p.nome,
        categoriaStock: p.categoriaStock,
        criadoPor: auth.userId,
        unidadeMedida: p.unidadeMedida,
        classificacao: p.classificacao || "PA",
      }));

      // Adicionar novos produtos, se houver
      if (novosProdutosBackend.length > 0) {
        const formData = new FormData();
        formData.append("produtos", JSON.stringify(novosProdutosBackend));

        const produtosRetornados = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/produtos/addmany`,
          "POST",
          formData,
          {
            Authorization: "Bearer " + auth.token,
          },
        );

        // Processar produtos retornados
        produtosRetornados.produtos.forEach((p, index) => {
          const originalProduto = novosProdutos[index];

          /* Um movimento por LOTE, não só pelo primeiro: um produto novo pode
             agora trazer vários lotes do mesmo ficheiro, e o que não tivesse
             movimento entrava no stock sem deixar rasto no histórico. */
          originalProduto.lotes.forEach((lote) => {
            movimentosStock.push({
              clinica: clinica.clinica._id,
              produto: p._id,
              lote: lote.lote,
              dataValidade: lote.dataValidade,
              responsavelMovimento: {
                tipo: "User",
                responsavel: auth.userId,
              },
              quantidade: lote.quantidade,
              data: new Date(),
              tipoMovimento: "entrada",
              precoCompra: lote.precoCompra || 0,
              fornecedor: lote.fornecedor || null,
              localizacaoDestino: lote.localizacoes?.[0]?.localizacao || null,
            });
          });

          // Adicionar ao stock atualizado
          updatedStock.push({
            produto: p._id,
            lotes: originalProduto.lotes,
            quantidadeTotal: originalProduto.quantidadeTotal,
            quantidadeIdeal: originalProduto.quantidadeIdeal,
          });
        });
      }

      /* O stock que veio do servidor traz o produto POPULADO, e era assim que
         seguia de volta: um objeto inteiro onde o servidor espera um id. Ele
         aguentava para procurar o produto, mas não para o comparar com o que já
         estava no stock — e acrescentava-o outra vez. Vai só o id. */
      const updatedDataParaEnviar = updatedStock.map((item) => ({
        ...item,
        produto: item.produto?._id || item.produto,
      }));

      // Atualizar stock com novos dados
      const formData = new FormData();
      formData.append("updatedData", JSON.stringify(updatedDataParaEnviar));
      formData.append("historicoMovimentos", JSON.stringify(movimentosStock));
      const stockAtualizado = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/stocks/${stock._id}`,
        "PATCH",
        formData,
        { Authorization: "Bearer " + auth.token },
      );

      // Processar adição de despesa, se necessário
      if (adicionarComoDespesa && custoTotal > 0) {
        try {
          const formData = new FormData();
          formData.append("clinica", clinica.clinica._id);
          formData.append(
            "observacoes",
            "Despesa gerada automaticamente da importação de stock",
          );
          formData.append(
            "descricao",
            descricao || "Compra de produtos para stock",
          );
          formData.append("valor", custoTotal);
          formData.append("dataLimitePagamento", new Date().toISOString());
          formData.append("categoria", categoria);
          formData.append("caixa", caixa);
          formData.append("criadoPor", auth.userId);

          if (fornecedorDespesa) {
            formData.append("fornecedor", fornecedorDespesa);
          }

          if (despesaPaga) {
            formData.append("dataPagamento", new Date().toISOString());
            formData.append("metodoPagamento", metodoPagamento);
            formData.append("despesaPaga", true);
            formData.append("pagaPor", auth.userId);
          } else {
            formData.append("despesaPaga", false);
          }

          await sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/despesas`,
            "POST",
            formData,
            {
              Authorization: "Bearer " + auth.token,
            },
          );

          toast.success("Despesa adicionada com sucesso");
        } catch (err) {
          console.error("Erro ao criar despesa:", err);
          // Não interrompemos o fluxo por erro na despesa, apenas notificamos
          toast.error(
            "Erro ao registrar despesa, mas produtos foram importados",
          );
        }
      }

      // Exibir mensagem de sucesso e atualizar o estado do stock
      atualizarStock(
        stockAtualizado.stock,
        `Importação concluída com sucesso! ${novosProdutos.length} novos produtos e ${produtosExistentes.length} produtos atualizados.`,
      );

      // Fechar modal
      handleCloseModal();
    } catch (err) {
      console.error("Erro na importação:", err);
      toast.error("Ocorreu um erro durante a importação");
    } finally {
      setIsSubmitting(false);
      stopLoading();
    }
  };

  const handleMethodPaymentChange = (value) => {
    setMetodoPagamento(value);
  };

  const handleCustoTotalChange = (value) => {
    setCustoTotal(value);
  };

  const handleDescricaoChange = (value) => {
    setDescricao(value);
  };

  // Funções para editar itens específicos nos dados processados

  const handleItemNameChange = (itemId, value) => {
    updateProcessedItem(itemId, "nome", value);
  };

  const handleItemQuantityChange = (itemId, value) => {
    updateProcessedItem(itemId, "quantidade", safeNumberConversion(value));
  };

  const handleItemIdealQuantityChange = (itemId, value) => {
    updateProcessedItem(itemId, "quantidadeIdeal", safeNumberConversion(value));
  };

  const handleItemBatchChange = (itemId, value) => {
    updateProcessedItem(itemId, "lote", value || "N/A");
  };

  const handleItemExpiryDateChange = (itemId, date) => {
    updateProcessedItem(itemId, "dataValidade", date ? date.toDate() : null);
  };

  const handleItemUnitChange = (itemId, value) => {
    updateProcessedItem(itemId, "unidadeMedida", value);
  };

  const handleItemCategoryChange = (itemId, value) => {
    // Garantir que o valor seja uma string - importante para comparações
    const normalizedValue =
      value !== null && value !== undefined ? String(value) : "";
    updateProcessedItem(itemId, "categoriaStock", normalizedValue);
  };
  const handleItemPriceChange = (itemId, value) => {
    updateProcessedItem(itemId, "precoCompra", safeNumberConversion(value));

    // Recalcular custo total
    const newTotal = processedData.reduce((sum, item) => {
      const price =
        item.id === itemId ? safeNumberConversion(value) : item.precoCompra;
      return sum + price * item.quantidade;
    }, 0);

    setCustoTotal(newTotal);
  };

  const handleItemSupplierChange = (itemId, value) => {
    updateProcessedItem(itemId, "fornecedor", value);
  };

  const handleItemClassificacaoChange = (itemId, value) => {
    updateProcessedItem(itemId, "classificacao", value);
  };

  const handleItemArmazemChange = (itemId, armazemId) => {
    // Ao mudar o armazém, limpar a localização e pré-seleccionar a primeira
    const primeiraLoc = localizacoesList
      .filter((l) => l.armazem?.id === armazemId || l.armazem === armazemId)
      .find((l) => l.ativo);
    updateProcessedItem(itemId, "armazem", armazemId);
    updateProcessedItem(itemId, "localizacao", primeiraLoc?.id || "");
  };

  const handleItemLocalizacaoChange = (itemId, localizacaoId) => {
    updateProcessedItem(itemId, "localizacao", localizacaoId);
  };

  // Funções utilitárias para construir e processar a árvore de categorias

  // Construir árvore de categorias a partir da lista plana
  const buildCategoryTree = (categories, parentId = null) => {
    if (!categories || !Array.isArray(categories)) return [];

    // Modificar o filtro para lidar com undefined e null corretamente
    return categories
      .filter((category) => {
        // Se parentId for null, aceitar tanto null quanto undefined
        if (parentId === null) {
          return (
            category.categoriaPai === null ||
            category.categoriaPai === undefined
          );
        }
        // Caso contrário, comparar normalmente
        return category.categoriaPai === parentId;
      })
      .map((category) => ({
        ...category,
        subcategorias: buildCategoryTree(categories, category.id),
      }));
  };
  // Renderizar opções de categoria com indentação para subcategorias
  const renderCategoryOptions = (categories, level = 0) => {
    if (!categories || !Array.isArray(categories)) return [];

    // Usar flatMap para criar um array plano a partir da estrutura hierárquica
    return categories.flatMap((category) => {
      // Primeiro, adicionar esta categoria
      const result = [
        {
          id: category.id,
          label: category.nome,
          level: level,
          value: category.id,
        },
      ];

      // Depois adicionar subcategorias se existirem
      if (
        category.subcategorias &&
        Array.isArray(category.subcategorias) &&
        category.subcategorias.length > 0
      ) {
        result.push(
          ...renderCategoryOptions(category.subcategorias, level + 1),
        );
      }

      return result;
    });
  };
  return {
    // Estado
    stock,
    currentStep,
    file,
    isSubmitting,
    errorMessage,
    processedData,
    originalData,
    detectedColumns,
    columnMapping,
    hasHeaderRow,
    errors,
    categoriaPrincipal,
    adicionarComoDespesa,
    despesaPaga,
    custoTotal,
    caixa,
    categoria,
    descricao,
    metodoPagamento,
    caixas,
    categoriasFinanceiro,
    fornecedoresList,
    armazensListImport,
    localizacoesList,
    armazemPadrao,
    localizacaoPadrao,
    isProcessingData,
    usarLotesAutomaticos,
    fornecedorDespesa,

    // Handlers
    handleFileChange,
    toggleHeaderRow,
    updateColumnMapping,
    handleNextStep,
    handlePreviousStep,
    handleSubmitImport,
    handleCategoriaPrincipalChange,
    handleMethodPaymentChange,
    handleCustoTotalChange,
    handleDescricaoChange,
    handleItemNameChange,
    handleItemQuantityChange,
    handleItemIdealQuantityChange,
    handleItemBatchChange,
    handleItemExpiryDateChange,
    handleItemUnitChange,
    handleItemCategoryChange,
    handleItemPriceChange,
    handleItemSupplierChange,
    handleItemClassificacaoChange,
    handleItemArmazemChange,
    handleItemLocalizacaoChange,
    fetchFornecedores,
    fetchArmazensELocalizacoes,
    handleFornecedorPadraoChange,
    handleArmazemPadraoChange,
    handleLocalizacaoPadraoChange,

    // Setters
    setAdicionarComoDespesa,
    setDespesaPaga,
    setCaixa,
    setCategoria,
    setUsarLotesAutomaticos,
    setFornecedorDespesa,

    // Utils
    buildCategoryTree,
    renderCategoryOptions,
    normalizeUnidadeMedida,

    // Constants
    UNIDADES_MEDIDA,
    FORMAS_PAGAMENTO,
  };
}

export default EnhancedImportStock;
